/**
 * Mock 模式开关 · W2-9 PR-1 mock 基础设施
 *
 * 承接：CLAUDE.md §11.5.1 数据工厂 / 场景脚本（admin /dev-console 子页）
 *
 * 触发方式（优先级从高到低）：
 *   1. URL `?mock=1` · 临时进入 mock 模式（一次性）
 *   2. URL `?mock=0` · 强制退出 mock 模式（覆盖 localStorage）
 *   3. localStorage `cs_admin_mock_mode=1` · 仅本地开发持久化（生产环境禁用）
 *
 * 设计意图：
 *   - PM 离线 / 后端未投产时也能跑 M01 链路调试
 *   - Playwright E2E 不依赖后端，CI 稳定
 *   - 真投产 PG 路径（/admin/v1/dev/seed/*）独立，PM 想验证真后端时显式调
 */

const STORAGE_KEY = "cs_admin_mock_mode";
const URL_PARAM = "mock";
const PERSISTED_MOCK_MODE_ENABLED = import.meta.env.DEV;

/**
 * 从当前 URL + localStorage 解析 mock 模式状态
 *
 * URL `?mock=1` 优先，其次 localStorage；URL `?mock=0` 显式关闭并清 storage。
 */
export function readMockModeFromEnv(
	urlSearch: string = typeof window !== "undefined" ? window.location.search : "",
	storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = typeof window !==
	"undefined"
		? window.localStorage
		: null,
	allowPersistedMock: boolean = PERSISTED_MOCK_MODE_ENABLED,
): boolean {
	const params = new URLSearchParams(urlSearch);
	const urlValue = params.get(URL_PARAM);

	if (urlValue === "1") {
		if (allowPersistedMock) storage?.setItem(STORAGE_KEY, "1");
		return true;
	}
	if (urlValue === "0") {
		storage?.removeItem(STORAGE_KEY);
		return false;
	}

	if (!allowPersistedMock) {
		storage?.removeItem(STORAGE_KEY);
		return false;
	}

	return storage?.getItem(STORAGE_KEY) === "1";
}

/**
 * 显式开启 mock 模式（用于 /dev-console 切换按钮）
 */
export function enableMockMode(
	storage: Pick<Storage, "setItem"> | null = typeof window !== "undefined"
		? window.localStorage
		: null,
): void {
	if (!PERSISTED_MOCK_MODE_ENABLED) return;
	storage?.setItem(STORAGE_KEY, "1");
}

/**
 * 显式关闭 mock 模式
 */
export function disableMockMode(
	storage: Pick<Storage, "removeItem"> | null = typeof window !== "undefined"
		? window.localStorage
		: null,
): void {
	storage?.removeItem(STORAGE_KEY);
}

/**
 * 模块加载时计算一次（运行时读 URL + localStorage）
 *
 * 注意：本值在页面加载时确定，运行中切换需要 `window.location.reload()`
 * 或调用 enableMockMode/disableMockMode 后刷新页面（避免 React Query 缓存错位）。
 */
export const MOCK_MODE: boolean = readMockModeFromEnv();
